import React, { useState, useMemo } from 'react';
import './Sidebar.css';
import { useNavigate } from 'react-router-dom';
import {
  FaHome, FaUsers, FaBoxOpen, FaChevronRight, FaUser, FaUserShield, FaUserTie,
  FaAddressBook, FaUserFriends, FaList, FaPlusCircle, FaPizzaSlice, FaShoppingBag,
  FaArrowDown, FaRandom, FaBalanceScale, FaStar, FaTags, FaMapMarkerAlt, FaMoneyBillWave, FaSitemap
} from 'react-icons/fa';

import { useAuth } from './auth/AuthContext.jsx';

// The menu items with the newly added permissions
const menuItems = [
  { name: 'Home', icon: <FaHome /> },
  {
    name: 'User Management',
    icon: <FaUsers />,
    subItems: [
      { name: 'Users', icon: <FaUser />, permission: 'users.view' },
      { name: 'Roles', icon: <FaUserShield />, permission: 'roles.view' },
     
    ]
  },
  {
    name: 'Contacts',
    icon: <FaAddressBook />,
    subItems: [
      { name: 'Customers', icon: <FaUserFriends />, permission: 'customers.view' },
    ]
  },
  {
    name: 'Products',
    icon: <FaBoxOpen />,
    subItems: [
      { name: 'Add Product', icon: <FaPlusCircle />, permission: 'raw_products.create' },
      { name: 'List Products', icon: <FaList />, permission: 'raw_products.view' },
      { name: 'Grocery', icon: <FaSitemap />, permission: 'categories.view' },
    ]
  },
  // {
  //   name: 'Categories',
  //   icon: <FaPizzaSlice />,
  //   subItems: [
  //       { name: 'Grocery', icon: <FaSitemap />, permission: 'categories.view' },
  //   ]
  // },
  {
    name: 'Food & Packages',
    icon: <FaPizzaSlice />,
    subItems: [
      { name: 'List Food Items',icon: <FaShoppingBag />, permission: 'food_items.view'},
      { name: 'Add Food Item', icon: <FaPlusCircle />, permission: 'food_items.create' },
      { name: 'List Packages', icon: <FaShoppingBag />, permission: 'packages.view' },
      { name: 'Add Package', icon: <FaPlusCircle />, permission: 'packages.create' },
    ]
  },
  {
    name: 'Inventory',
    icon: <FaArrowDown />,
    subItems: [
      { name: 'GRN', icon: <FaList />, permission: 'inventory.view' },
      { name: 'Add GRN', icon: <FaPlusCircle />, permission: 'inventory.create' },
    ]
  },
   {
    name: 'Subscriptions',
    icon: <FaList />,
    subItems: [
      { name: 'Subscribers', icon: <FaList />, permission: 'subscriptions.view' },
      { name: 'Subscribe Request', icon: <FaPlusCircle />, permission: 'subscriptions.manage' },
    ]
  },
  {
    name: 'Hub Management',
    icon: <FaRandom />,
    subItems: [
      { name: 'Hubs', icon: <FaList />, permission: 'hubs.view' },
      { name: 'Hub Requests', icon: <FaList />, permission: 'hubs.manage' },
      { name: 'Create hubs', icon: <FaPlusCircle />, permission: 'hubs.create' },
    ]
  },
  {
    name: 'Delivery',
    icon: <FaBalanceScale />,
    subItems: [
      { name: 'Assign Delivery', icon: <FaList />, permission: 'delivery.assign' },
      { name: 'Delivery Person', icon: <FaPlusCircle />, permission: 'delivery_person.manage' },
    ]
  },
  {
    name: 'Kitchen',
    icon: <FaStar />,
    subItems: [
      { name: 'Kitchen Dashboard', icon: <FaStar />, permission: 'kitchen.dashboard' },
      { name: 'Cooking Request', icon: <FaPizzaSlice />, permission: 'kitchen.requests' }
    ]
  },
  {
    name: 'Banner',
    icon: <FaTags />,
    permission: 'banner.manage'
  },
  {
    name: 'Meals',
     icon: <FaMapMarkerAlt />,
    subItems: [
      { name: 'Tomorrow Meals', icon: <FaList />, permission: 'meals.view' }
    ]
  },
  {
    name: 'Expenses',
    icon: <FaMoneyBillWave />,
    subItems: [
      { name: 'List Expenses', icon: <FaList />, permission: 'expenses.view' },
      { name: 'Add Expense', icon: <FaPlusCircle />, permission: 'expenses.create' },
    ]
  },
];

const navigateMap = {
  Home: '/dashboard', Users: '/users', Roles: '/roles', 'List Products': '/product', 'Add Product': '/add',
  'Grocery': '/categories', 'List Food Items': '/food-items',
  'List Packages': '/packages' , 'GRN': '/grns' , 'Add GRN': '/grns/add' , 'List Expenses': '/expenses',
  'Add Expense': '/expenses/add', 'Customers': '/Customers', 'Subscribe Request': '/package-requests',
  'Subscribers': '/subscribers', 'Kitchen Dashboard': '/kitchen-dashboard', 'Cooking Request': '/cooking-requests',
  'Banner': '/banner', 'Assign Delivery': '/assign-delivery', 'Delivery Person': '/delivery-person',
  'Hubs': '/hub-view', 'Hub Requests': '/hub-requests', 'Create hubs': '/create-hub',
  'Tomorrow Meals': '/tomorrows-meals',
  'Add Food Item': '/food-items/add',
  'Add Package': '/packages/add',
};

const Sidebar = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const [activeItem, setActiveItem] = useState('Home');
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const accessibleMenuItems = useMemo(() => {
    const filterItems = (items) => {
      return items.reduce((acc, item) => {
        if (item.subItems) {
          const accessibleSubItems = filterItems(item.subItems);
          if (accessibleSubItems.length > 0) {
            acc.push({ ...item, subItems: accessibleSubItems });
          }
        } 
        else if (!item.permission || hasPermission(item.permission)) {
          acc.push(item);
        }
        return acc;
      }, []);
    };
    return filterItems(menuItems);
  }, [hasPermission]);

  const toggleSubItems = (itemName) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const handleNavigation = (item) => {
    setActiveItem(item.name);
    if (navigateMap[item.name]) {
      navigate(navigateMap[item.name]);
    }
  };

  return (
    <div className="sidebar">
      <a href="/dashboard" className="sidebar-header">
        <div className="sidebar-logo"><FaBoxOpen size={24} /></div>
        <h2 className="sidebar-title">SUGAN FOODS</h2>
      </a>

      <ul className="menu-list">
        {accessibleMenuItems.map((item, index) => {
          const isExpanded = expandedItems[item.name];
          const isActive = activeItem === item.name || (item.subItems && item.subItems.some(sub => activeItem === sub.name));

          return (
            <React.Fragment key={index}>
              <li className="menu-item-wrapper">
                <div 
                  className={`menu-item ${isActive ? 'menu-item--active' : ''}`}
                  onClick={() => {
                    if (item.subItems) {
                      toggleSubItems(item.name);
                    } else {
                      handleNavigation(item);
                    }
                  }}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (item.subItems) {
                        toggleSubItems(item.name);
                      } else {
                        handleNavigation(item);
                      }
                    }
                  }}
                >
                  <div className="menu-item-content">
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-item-text">{item.name}</span>
                  </div>
                  {item.subItems && (
                    <FaChevronRight className={`chevron ${isExpanded ? 'chevron--expanded' : ''}`} />
                  )}
                </div>

                {item.subItems && isExpanded && (
                  <ul className="sub-menu-list">
                    {item.subItems.map((subItem, i) => (
                      <li 
                        key={i}
                        className={`sub-menu-item ${activeItem === subItem.name ? 'sub-menu-item--active' : ''}`}
                        onClick={() => handleNavigation(subItem)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleNavigation(subItem);
                          }
                        }}
                      >
                        <span className="menu-icon">{subItem.icon}</span>
                        <span>{subItem.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
};

export default Sidebar;